import { ColumnDef } from '@tanstack/react-table';
import {
  ContainerMaterialMatrixData,
  ContainerPeriodPricingData,
  DiscountData,
  PricingRule,
  ConfigurationTypeEnum,
  TonnagePricingData,
  DiscountTypeEnum,
  CustomerClassificationEnum,
  PayAsYouGoPricingData,
} from '@libs/schemas';
import { RuleTypeBadge } from 'components/pricing/badges/RuleTypeBadge';
import { StatusBadge } from 'components/pricing/badges/StatusBadge';
import { RuleActionsMenu } from 'components/pricing/RuleActionsMenu';
import { DataTableColumnHeader } from 'components/table/DataTableColumnHeader';
import { safeMoneyFromCents } from '@libs/services/money';
import { format } from 'date-fns';

export const createPricingRulesColumns = (
  onEdit: (rule: PricingRule) => void,
  onDelete?: (rule: PricingRule) => void,
  onDuplicate?: (rule: PricingRule) => void,
): ColumnDef<PricingRule>[] => [
  {
    accessorKey: 'name',
    header: 'Rule Name',
    cell: ({ row }) => (
      <div className="flex flex-col">
        <span className="font-medium">{row.getValue('name')}</span>
        <div className="flex flex-wrap gap-1 mt-1">
          <RuleTypeBadge key={row.original._id} type={row.original.configuration.type} />
        </div>
      </div>
    ),
  },
  {
    accessorKey: 'configuration',
    header: 'Value',
    cell: ({ row }) => {
      const config = row.original.configuration;

      const configData = config.data;
      let displayValue = '';

      if (!configData) {
        return <span className="text-muted">Invalid configuration</span>;
      }

      switch (config.type) {
        case ConfigurationTypeEnum.CONTAINER_MATERIAL_MATRIX: {
          const matrixData = configData as ContainerMaterialMatrixData;
          if (matrixData.matrixData) {
            displayValue = 'Single material pricing (Fixed)';
          } else {
            displayValue = 'Single material not configured';
          }
          break;
        }
        case ConfigurationTypeEnum.CONTAINER_PERIOD_PRICING: {
          const periodData = configData as ContainerPeriodPricingData;
          if (periodData.containerPeriods) {
            const containerCount = Object.keys(periodData.containerPeriods).length;
            displayValue = `Flat rate package (${containerCount} container${containerCount !== 1 ? 's' : ''})`;
          } else {
            displayValue = 'Flat rate not configured';
          }
          break;
        }
        case ConfigurationTypeEnum.TONNAGE_PRICING: {
          const tonnageData = configData as TonnagePricingData;
          if (tonnageData.perTonRateInCents && tonnageData.minimumTonnage) {
            const rate = safeMoneyFromCents(tonnageData.perTonRateInCents).toFormat();
            displayValue = `${rate}/ton (min ${tonnageData.minimumTonnage} tons)`;
          } else {
            displayValue = 'Tonnage pricing not configured';
          }
          break;
        }
        case ConfigurationTypeEnum.AUTO_DISCOUNT:
        case ConfigurationTypeEnum.DISCOUNT: {
          const discountData = configData as DiscountData;

          if (
            discountData.discountType === DiscountTypeEnum.PERCENTAGE &&
            discountData.discountPercent
          ) {
            displayValue = `${discountData.discountPercent.toFixed(0)}% off`;
          } else if (
            discountData.discountType === DiscountTypeEnum.FLAT &&
            discountData.discountAmountInCents
          ) {
            displayValue = `${safeMoneyFromCents(discountData.discountAmountInCents).toFormat()} off`;
          } else {
            displayValue = 'Discount configured';
          }
          break;
        }
        case ConfigurationTypeEnum.PAY_AS_YOU_GO_PRICING: {
          const payAsYouGoData = configData as PayAsYouGoPricingData;
          if (payAsYouGoData.containerRates) {
            const containerCount = Object.keys(payAsYouGoData.containerRates).length;
            displayValue = `Pay as you go pricing (${containerCount} container${containerCount !== 1 ? 's' : ''})`;
          } else {
            displayValue = 'Pay as you go pricing not configured';
          }
          break;
        }
        default:
          displayValue = 'Unknown configuration';
      }

      return <span className="text-sm">{displayValue}</span>;
    },
  },
  {
    accessorKey: 'scope',
    header: 'Scope',
    cell: ({ row }) => {
      const scope = row.original.scope;
      const parts: string[] = [];

      if (scope.zipCodes && scope.zipCodes !== CustomerClassificationEnum.ALL) {
        parts.push(`${scope.zipCodes.length} zip codes`);
      }
      if (scope.customerIds && scope.customerIds !== CustomerClassificationEnum.ALL) {
        parts.push(`${scope.customerIds.length} customers`);
      }
      if (
        scope.customerClassification &&
        scope.customerClassification !== CustomerClassificationEnum.ALL
      ) {
        parts.push(scope.customerClassification);
      }

      return <span className="text-sm">{parts.join(', ') || 'All'}</span>;
    },
  },
  {
    accessorKey: 'precedence',
    header: ({ column }) => <DataTableColumnHeader column={column} title="Precedence" />,
    cell: ({ row }) => (
      <div className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
        {row.getValue('precedence')}
      </div>
    ),
  },
  {
    accessorKey: 'active',
    header: 'Status',
    cell: ({ row }) => (
      <StatusBadge active={row.getValue('active')} archived={row.original.archived} />
    ),
  },
  {
    accessorKey: 'effectiveFrom',
    header: 'Effective Period',
    cell: ({ row }) => {
      const from = new Date(row.getValue('effectiveFrom'));
      const to = row.original.effectiveTo ? new Date(row.original.effectiveTo) : null;

      return (
        <span className="text-sm">
          {format(from, 'MM/dd/yyyy')}
          {' to '}
          {to ? format(to, 'MM/dd/yyyy') : 'indefinite'}
        </span>
      );
    },
  },
  {
    id: 'actions',
    cell: ({ row }) => (
      <div className="flex items-center gap-2">
        <RuleActionsMenu
          rule={row.original}
          onEdit={onEdit}
          onDelete={onDelete}
          onDuplicate={onDuplicate}
        />
      </div>
    ),
  },
];
